import { memo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Briefcase, Building2, Globe, Mail, MapPin, Phone, User, UserCircle } from 'lucide-react';

import ReviewRow from './ReviewRow.jsx';
import SandboxNotice from './SandboxNotice.jsx';

const CHIP_CLASS = 'inline-flex rounded-md bg-surface-muted px-2 py-0.5 text-sm text-content';

/** A value shown as a small tag, or nothing when it is empty (the row then says "None"). */
const chip = (value) => (value ? <span className={CHIP_CLASS}>{value}</span> : null);

/** Step 3: everything that will be sent, read-only. Going back to change it is the stepper's and Back's job. */
function ReviewStep({ entityType, values }) {
  const { t } = useTranslation('unified-profiles');
  const field = (key) => t(`create.fields.${key}.label`);

  const isCustomer = entityType === 'customers';
  const filledPhones = (values.phones ?? []).filter((phone) => phone.number.trim());

  const phoneValue =
    filledPhones.length > 0 ? (
      <ul className="flex flex-wrap gap-2">
        {filledPhones.map((phone) => (
          <li key={phone.id}>
            {chip(
              phone.isPrimary && filledPhones.length > 1
                ? t('create.review.primaryPhone', { number: phone.number })
                : phone.number,
            )}
          </li>
        ))}
      </ul>
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-content">{t('create.review.title')}</h2>
        <p className="text-sm text-content-muted">{t('create.review.help')}</p>
      </div>

      <dl className="flex flex-col gap-2">
        {isCustomer ? (
          <>
            <ReviewRow icon={User} label={t('create.review.rows.customer')}>
              {values.fullName}
            </ReviewRow>
            <ReviewRow icon={Mail} label={field('email')}>
              {chip(values.email)}
            </ReviewRow>
            <ReviewRow icon={Phone} label={t('create.fields.phones.legend')}>
              {phoneValue}
            </ReviewRow>
            <ReviewRow icon={MapPin} label={field('location')}>
              {chip(values.location)}
            </ReviewRow>
          </>
        ) : (
          <>
            <ReviewRow icon={Building2} label={t('create.review.rows.company')}>
              {values.name}
            </ReviewRow>
            <ReviewRow icon={Globe} label={field('domain')}>
              {chip(values.domain)}
            </ReviewRow>
            <ReviewRow icon={Briefcase} label={field('industry')}>
              {chip(values.industry)}
            </ReviewRow>
            <ReviewRow icon={MapPin} label={field('city')}>
              {chip(values.city)}
            </ReviewRow>
          </>
        )}
        <ReviewRow icon={UserCircle} label={field('owner')}>
          {values.ownerName}
        </ReviewRow>
      </dl>

      <SandboxNotice name={isCustomer ? values.fullName : values.name} />
    </div>
  );
}

export default memo(ReviewStep);
